import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    BaseAsset,
    IconAsset,
    MediaSourceData,
    SceneCategory,
    SceneGroup,
    Slide,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { NarrativeThreadEnum } from 'js/src/client';
import type { DASHBOARD_VIDEO_INIT_TYPE, OPTIONS_PAGE_VIDEO_INIT_TYPE } from 'js/src/data/app';
import { VIDEO_INIT_TYPE } from 'js/src/data/app';
import { isAudioSourceData } from 'js/src/data/media';
import type { ConfigurableElementProperties, DynamicStyleConfig } from 'js/src/luminary';
import {
    getRootAssetGroup,
    getVoiceDrivenAssetSourceData,
    isAIAvatarSceneGroup,
    isAIVoiceoverSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
} from 'js/src/luminary';

// utility method to clamp a number between a min & max number
const _clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

/**
 * Resize all text assets on a scene so they fit as large a space as possible within the scene without being truncated.
 */
export function resizeTextToFitOnScene(scene: Slide): void {
    // check to ensure the scene's autoUpdate is true otherwise it's likely we're in a `batchUpdate()` loop which
    // means the text assets haven't had a chance to update their sizes making this call effectively a `noop`
    let turnAutoUpdateOff = false;
    if (!scene.autoUpdate) {
        // eslint-disable-next-line no-console
        console.warn(
            'Cannot resize text to fit on scene when scene.autoUpdate is false (turning it on temporarily)',
        );
        scene.autoUpdate = true;
        turnAutoUpdateOff = true;
    }

    scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((text) => text.isEditable)
        .forEach((textAsset) => {
            textAsset.setFontSizeToFitAllText();
        });

    if (turnAutoUpdateOff) {
        scene.autoUpdate = false;
    }
}

/**
 * Get text that will be used for media query suggestions. If it's a regular scene,
 * combine and return all text from it's scene & subscenes.
 * If it's a voice-driven scene, use the captions within the scene.
 */
export function getSceneTextForMediaQuery(scene: Slide): string {
    let aggregatedText = '';
    const captions = scene.sceneGroup.captions;
    if (isVoiceDrivenSceneGroup(scene.sceneGroup) && captions) {
        const sceneStartTime = scene.startTimeRelativeToSceneGroup;
        const sceneEndTime = scene.endTimeRelativeToSceneGroup;
        let startIndex = 0;
        let endIndex = 0;
        const transcriptWords = scene.sceneGroup.captions.asset.transcript.words;
        // Get and aggregate all caption text that fall within the scene duration
        for (let i = 0; i < transcriptWords.length; i++) {
            const word = transcriptWords[i];
            if (word.endTime > sceneStartTime && word.startTime < sceneEndTime) {
                if (!aggregatedText) {
                    startIndex = i;
                }
                endIndex = i;
                aggregatedText = aggregatedText + ' ' + word.word.trim();
            }
        }
        aggregatedText = aggregatedText.trim();
        // We want to try and return full sentences so we check the previous and next caption blocks to see if we can terminate at a sentence break.
        const sentenceBreaks = ['.', '?', '!'];
        let currentIndex = endIndex + 1;
        // Only check next block if current block doesn't already terminate on a sentence break.
        if (
            currentIndex < transcriptWords.length &&
            (!aggregatedText ||
                !sentenceBreaks.includes(aggregatedText.charAt(aggregatedText.length - 1)))
        ) {
            const text = transcriptWords[currentIndex].word.trim();
            let i = 0;
            // Loop forward through each character in next text block and add text until we find a sentence break, otherwise add all text.
            while (i < text.length) {
                if (sentenceBreaks.includes(text.charAt(i))) {
                    i += 1;
                    break;
                }
                i += 1;
            }
            aggregatedText = aggregatedText + ' ' + text.substring(0, i).trim();
        }
        currentIndex = startIndex - 1;
        const previousText = currentIndex >= 0 ? transcriptWords[currentIndex].word.trim() : '';
        // Only check previous block if current block isn't a new sentence. i.e if previous text doesn't terminate at a sentence break
        if (
            previousText &&
            !sentenceBreaks.includes(previousText.charAt(aggregatedText.length - 1))
        ) {
            const text = transcriptWords[currentIndex].word.trim();
            let i = text.length - 1;
            // Loop backward and add text in previous blocks until we find a sentence break
            while (i >= 0) {
                if (sentenceBreaks.includes(text.charAt(i))) {
                    break;
                }
                i -= 1;
            }
            aggregatedText = text.substring(i + 1, text.length).trim() + ' ' + aggregatedText;
        }
    } else {
        scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter((text) => text.isEditable)
            .forEach((textAsset) => {
                aggregatedText += textAsset.text.trim() + ' ';
            });
    }
    return aggregatedText.trim();
}

/**
 * Gets the max position where we can add a scene group, considering that we can't add after an outro
 * or after a branding scene.
 */
export function getMaxSceneGroupTargetPosition(luminary: Luminary) {
    const hasOutroScene = luminary.getOutroSceneGroup() !== undefined;
    const hasBrandingScene =
        luminary.scenes.find((scene) => scene.sceneCategory === VideoData.CATEGORY_BRAND) !==
        undefined;
    const maxIndex =
        luminary.sceneGroups.length - 1 - (hasOutroScene ? 1 : 0) - (hasBrandingScene ? 1 : 0);

    return maxIndex;
}

/**
 * Gets the correct targetIndex for a specified targetIndex, correcting to makes ure it's within the video
 */
export function getTargetIndex(luminary: Luminary, targetIndex: number | null) {
    const maxTargetIndex = getMaxSceneGroupTargetPosition(luminary);
    if (targetIndex === null) {
        return maxTargetIndex;
    }
    if (targetIndex < 0) {
        return 0;
    }
    return Math.min(targetIndex, maxTargetIndex);
}

export type NormalizedAssetBounds = {
    normalizedWidth: number;
    normalizedHeight: number;
    normalizedX: number;
    normalizedY: number;
};
/**
 * Returns the bounds (x, y, width, height) for a Luminary asset normalized as a percentage (e.g. 0.5).
 * For assets within an asset group, the bounds are relative to the asset group's bounds, while
 * assets not in a group are relative to the scene's bounds.
 */
export function getNormalizedAssetBounds(
    asset: Asset | null | undefined,
): NormalizedAssetBounds & { leftOutOfBounds: boolean; rightOutOfBounds: boolean } {
    if (!asset) {
        return {
            normalizedWidth: -1,
            normalizedHeight: -1,
            normalizedX: -1,
            normalizedY: -1,
            leftOutOfBounds: true,
            rightOutOfBounds: true,
        };
    }
    const isTextOrGroupAsset = VideoData.isTextAsset(asset) || VideoData.isAssetGroup(asset);
    let xCoord;
    let yCoord;
    let containerWidth;
    let containerHeight;
    if (isTextOrGroupAsset) {
        if (asset.hasUserChangedMaxWidth) {
            const { x, width } = asset.approximateMaxBounds;
            const { y, height } = asset.boundsWithoutAnimation;
            xCoord = x;
            yCoord = y;
            containerHeight = height;
            containerWidth = width;
        } else {
            const { x, width, y, height } = asset.boundsWithoutAnimation;
            xCoord = x;
            yCoord = y;
            containerHeight = height;
            containerWidth = width;
        }
    } else {
        const { x, y, height, width } = asset.approximateMaxBounds; // TODO: better bounds calculation! (tricky w/ masks)
        xCoord = x;
        yCoord = y;
        containerHeight = height;
        containerWidth = width;
    }
    const rootAssetGroup = getRootAssetGroup(asset);

    let relativeWidth = asset.root.referenceResolution.width;
    let relativeHeight = asset.root.referenceResolution.height;
    let relativeX = xCoord;
    let relativeY = yCoord;

    if (rootAssetGroup) {
        const assetGroupBounds = rootAssetGroup.boundsWithoutAnimation;
        relativeWidth = assetGroupBounds.width;
        relativeHeight = assetGroupBounds.height;
        relativeX -= assetGroupBounds.x;
        relativeY -= assetGroupBounds.y;
    }

    const leftOutOfBounds = relativeX < 0;
    const topOutOfBounds = relativeY < 0;
    const rightOutOfBounds = relativeX + containerWidth > relativeWidth;
    const bottomOutOfBounds = relativeY + containerHeight > relativeHeight;
    if (leftOutOfBounds) {
        containerWidth += relativeX;
        relativeX = Math.max(0, relativeX);
    }
    if (topOutOfBounds) {
        containerHeight += relativeY;
        relativeY = Math.max(0, relativeY);
    }
    if (rightOutOfBounds) {
        containerWidth -= relativeX + containerWidth - relativeWidth;
    }
    if (bottomOutOfBounds) {
        containerHeight -= relativeY + containerHeight - relativeHeight;
    }

    return {
        normalizedWidth: _clamp(containerWidth / relativeWidth, 0, 1),
        normalizedHeight: _clamp(containerHeight / relativeHeight, 0, 1),
        normalizedX: _clamp(relativeX / relativeWidth, 0, 1),
        normalizedY: _clamp(relativeY / relativeHeight, 0, 1),
        leftOutOfBounds,
        rightOutOfBounds,
    };
}

/**
 * Returns true if sceneCategory is voice-driven
 */
export function isVoiceDrivenCompatibleSceneCategory(sceneCategory: SceneCategory): boolean {
    if (sceneCategory === VideoData.CATEGORY_COMMON) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_VOICEOVER) {
        return true;
    }
    if (sceneCategory === VideoData.CATEGORY_TALKING_HEAD) {
        return true;
    }
    return false;
}

/**
 * Please see {@link https://stackoverflow.com/questions/588004/is-floating-point-math-broken} first
 * Floating point subtraction with a tolerance level of 3 decimal places. For comparison, please @see floatingPointComparison
 * @param floatA - the floatA in floatA - floatB
 * @param floatB - floatB in floatA - floatB
 */
export function floatingPointSubtraction(floatA: number, floatB: number): number {
    const tolerance = 3;
    return Number.parseFloat((floatA - floatB).toFixed(tolerance));
}

/**
 * Function to compare floating points with a lenient tolerance (0.001). For comparison we're more lenient
 * than the subtraction operation because comparisons are usually used to call add/remove scenes, where as
 * arithmetic operations are used to set durations.
 * @see floatingPointSubtraction for floating point stack overflow post
 * @param floatA - a number
 * @param floatB - a number
 * @returns whether the two numbers are equal within tolerance level of 0.001
 */
export function floatingPointComparison(floatA: number, floatB: number): boolean {
    const tolerance = 0.001; // kind of arbitrary but essentially ~ toFixed(6)
    return Math.abs(floatA - floatB) < tolerance;
}

export function getNarrativeThreadFromVideoInitType(
    videoInitType: DASHBOARD_VIDEO_INIT_TYPE | OPTIONS_PAGE_VIDEO_INIT_TYPE,
): NarrativeThreadEnum {
    switch (videoInitType) {
        case VIDEO_INIT_TYPE.CREATE_FROM_SCRATCH:
            return NarrativeThreadEnum.TEXT; // defaults to a text scene
        case VIDEO_INIT_TYPE.VOICEOVER:
            return NarrativeThreadEnum.VOICEOVER;
        case VIDEO_INIT_TYPE.TALKING_HEAD:
            return NarrativeThreadEnum.TALKING_HEAD;
        case VIDEO_INIT_TYPE.TEXT_TO_VIDEO:
            return NarrativeThreadEnum.TEXT;
        case VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER:
            return NarrativeThreadEnum.VOICEOVER;
        case VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR:
            return NarrativeThreadEnum.TALKING_HEAD;
        default:
            throw new Error('Invalid video init type!');
    }
}

/**
 * Function that returns the VideoInitType for a given NarrativeThread
 * This is the reverse of getNarrativeThreadFromVideoInitType
 */
export function getVideoInitTypeFromNarrativeThread(
    narrativeThread: NarrativeThreadEnum,
): OPTIONS_PAGE_VIDEO_INIT_TYPE {
    switch (narrativeThread) {
        case NarrativeThreadEnum.TEXT:
            return VIDEO_INIT_TYPE.TEXT_TO_VIDEO;
        case NarrativeThreadEnum.VOICEOVER:
            return VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER;
        case NarrativeThreadEnum.TALKING_HEAD:
            return VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR;
        default:
            throw new Error('Invalid narrative thread!');
    }
}

/**
 * Function that returns the NarrativeThread for a given SceneGroup
 */
export function getNarrativeThreadForSceneGroup(sceneGroup: SceneGroup): NarrativeThreadEnum {
    if (isVoiceoverSceneGroup(sceneGroup)) {
        return NarrativeThreadEnum.VOICEOVER;
    }
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return NarrativeThreadEnum.TALKING_HEAD;
    }
    return NarrativeThreadEnum.TEXT;
}

export function setVoiceDrivenBaseAssetEndTime(sceneGroup: SceneGroup, endTimeMillis: number) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        sceneGroup.mediaAsset.endTime = endTimeMillis;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        sceneGroup.voiceoverAsset.endTime = endTimeMillis;
    }
}

export function setVoiceDrivenBaseAssetStartTime(sceneGroup: SceneGroup, startTimeMillis: number) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        sceneGroup.mediaAsset.startTime = startTimeMillis;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        sceneGroup.voiceoverAsset.startTime = startTimeMillis;
    }
}

export function hasExceededVideoDurationLimit(
    luminary: Readonly<Luminary>,
    videoDurationLimitSeconds: number | null,
): boolean {
    if (!videoDurationLimitSeconds) {
        return false;
    }
    return luminary.duration > videoDurationLimitSeconds * 1000;
}

export function getCurrentAIVOSecondsUsed(luminary: Readonly<Luminary>): number {
    return Math.floor(
        luminary.sceneGroups.reduce((duration, sceneGroup) => {
            if (isAIVoiceoverSceneGroup(sceneGroup) && !isAIAvatarSceneGroup(sceneGroup)) {
                duration += sceneGroup.duration;
            }
            return duration;
        }, 0) / 1000,
    );
}

export function resizeTextToFitOnSceneForEntireVideo(luminary: Luminary): void {
    luminary.scenes.forEach((scene: Slide) => resizeTextToFitOnScene(scene));
}

/**
 * Remove all the scenes & scene groups from the video
 */
export function clearScenes(luminary: Luminary, sceneGroupIdsToPreserve: string[]) {
    luminary.batchRun(() => {
        // clear old deprecated voiceover track if it exists
        luminary.voiceover = null;

        // remove all scene groups except those in sceneGroupIdsToPreserve
        const sceneGroupIds = luminary.sceneGroups.map((sceneGroup) => sceneGroup.id);
        const sceneGroupIdsToDelete = sceneGroupIds.filter(
            (sceneGroupId) => !sceneGroupIdsToPreserve.includes(sceneGroupId),
        );

        // we want to make sure that we don't delete all scenes (since that'll leave the Luminary object in
        // an invalid state for saving/previewing/publishing). So error if we're about to delete all scenes.
        if (sceneGroupIdsToDelete.length === sceneGroupIds.length) {
            throw new Error('Cannot delete all scenes');
        }

        sceneGroupIdsToDelete.forEach((sceneGroupId) => {
            luminary.removeSceneGroup(sceneGroupId);
        });
    });
}

export function clearScenesExceptCategories({
    luminary,
    sceneGroupIdsToPreserve,
    preserveOutroScenes = false,
}: {
    luminary: Luminary;
    sceneGroupIdsToPreserve: string[];
    preserveOutroScenes?: boolean;
}) {
    const sceneIdsToPreserveCategories = luminary.scenes
        .filter((scene) => {
            const isOutroScene = scene.sceneCategory === VideoData.CATEGORY_OUTRO;
            return preserveOutroScenes && isOutroScene;
        })
        .map((scene) => scene.id);
    // get list of scene group ids matching the categories we want to preserve
    const sceneIdsToPreserveSet = new Set(sceneIdsToPreserveCategories);
    const sceneGroupIdsToPreserveCategories = luminary.sceneGroups
        .filter((sceneGroup) => {
            return sceneGroup.scenes.some((scene) => sceneIdsToPreserveSet.has(scene.id));
        })
        .map((sceneGroup) => sceneGroup.id);

    const allSceneGroupIdsToPreserve = [
        ...sceneGroupIdsToPreserve,
        ...sceneGroupIdsToPreserveCategories,
    ];

    clearScenes(luminary, allSceneGroupIdsToPreserve);
}

const SCREEN_RECORDING_SUFFIX = '#screenRecording';

export function findScreenRecordingMediaAssetsInScene(scene: Slide): TimeAwareMediaAsset[] {
    const mediaAssets = scene
        .getAssets()
        .filter(VideoData.isMediaAsset)
        .filter((asset) => isScreenRecordingSourceData(asset.sourceData));
    return mediaAssets;
}

export function isScreenRecordingScene(scene: Slide): boolean {
    const mediaAssets = findScreenRecordingMediaAssetsInScene(scene);
    return mediaAssets.length > 0;
}

export function isScreenRecordingSceneGroup(sceneGroup: SceneGroup): boolean {
    if (!isVoiceDrivenSceneGroup(sceneGroup)) {
        return false;
    }
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    if (isAudioSourceData(sourceData)) {
        return false;
    }
    return isScreenRecordingSourceData(sourceData);
}

export function isScreenRecordingSourceData(
    sourceData: MediaSourceData | null | undefined,
): boolean {
    return hasScreenRecordingSuffix(sourceData?.source_id || '');
}

export function appendScreenRecordingSuffix(sourceId: string): string {
    return `${sourceId}${SCREEN_RECORDING_SUFFIX}`;
}

export function hasScreenRecordingSuffix(sourceId: string): boolean {
    return sourceId.endsWith(SCREEN_RECORDING_SUFFIX);
}

export function getMediaAssetSnapshot(
    luminary: Luminary,
    sceneIndices: number[],
): Map<string, string | null> {
    const snapshot = new Map<string, string | null>();

    sceneIndices.forEach((sceneIndex) => {
        const scene = luminary.scenes[sceneIndex];
        scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .filter((asset) => asset.isEditable)
            .forEach((asset) => {
                snapshot.set(`${sceneIndex}-${asset.name}`, asset.sourceData?.uuid || null);
            });
    });
    return snapshot;
}

/**
 * Checks if the outro scene group has any video media with volume enabled.
 * If so, sets the outro scene group's musicVolumeOverride to 0.
 * This prevents background music from conflicting with outro video audio.
 * @returns true if musicVolumeOverride was changed, false otherwise
 */
export function muteOutroMusicIfHasMediaWithVolume(luminary: Luminary): boolean {
    const outroSceneGroup = luminary.getOutroSceneGroup();
    if (!outroSceneGroup) {
        return false;
    }

    const sceneGroup = outroSceneGroup.sceneGroup;
    let hasMediaWithVolume = false;

    for (const scene of sceneGroup.scenes) {
        const mediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
        for (const asset of mediaAssets) {
            if (asset.isVideo && asset.volume > 0) {
                hasMediaWithVolume = true;
                break;
            }
        }
        if (hasMediaWithVolume) {
            break;
        }
    }

    if (hasMediaWithVolume && sceneGroup.musicVolumeOverride !== 0) {
        sceneGroup.musicVolumeOverride = 0;
        return true;
    }

    return false;
}

// Still not sure how we should handle this. Copy the current object's property or keep this logic?
export const saveDynamicStyle = (objectToSerialize: BaseAsset) => {
    const settings = {
        colorGraphics: objectToSerialize._savePropertyList(['colorGraphics']).colorGraphics,
        alphaGraphics: objectToSerialize._savePropertyList(['alphaGraphics']).alphaGraphics,
        objectAnimation: objectToSerialize._savePropertyList(['objectAnimation']).objectAnimation,
    };

    return settings;
};

export const loadDynamicStyle = (target: BaseAsset, settings: DynamicStyleConfig) => {
    // TODO: luminary - we should not call this method outside of luminary
    (target as any)._objectAnimationCustomLoad(settings.objectAnimation);

    const alphaGraphics = settings.alphaGraphics;
    const colorGraphics = settings.colorGraphics;

    // TODO: luminary - we should not call this method outside of luminary
    (target as any)._alphaGraphicsCustomLoad(alphaGraphics, {});
    // TODO: luminary - we should not call this method outside of luminary
    (target as any)._colorGraphicsCustomLoad(colorGraphics);
};

export function getAssetCenterXY(asset: Asset) {
    const anchor = asset.transformation.anchor;
    if (anchor.x === 0.5 && anchor.y === 0.5) {
        return {
            x: asset.transformation.position.x,
            y: asset.transformation.position.y,
        };
    }
    const bounds = asset.approximateMaxBounds;
    return {
        x: bounds.x + bounds.width * 0.5,
        y: bounds.y + bounds.height * 0.5,
    };
}

export function getElementPropertiesToSave(
    asset: TimeAwareMediaAsset | IconAsset,
    sceneDuration: number,
): ConfigurableElementProperties {
    const position = getAssetCenterXY(asset);
    const dimensions = {
        height: asset.maxHeight,
        width: asset.maxWidth,
    };
    if (asset.parentId) {
        dimensions.height = asset.height;
        dimensions.width = asset.width;
    }
    const introOutroTimes = {
        introTime: asset.introAnimationOffset,
        exitTime: sceneDuration - asset.outroAnimationOffset,
    };
    const dynamicStyles = asset.hasDynamicStyle ? saveDynamicStyle(asset) : null;
    let groupIndex = null;
    const assetGroup = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    if (assetGroup) {
        groupIndex = assetGroup.getAssetIndex(asset);
    }
    return {
        position,
        dimensions,
        introOutroTimes,
        dynamicStyles,
        groupIndex,
    };
}
